import "../styles/common.scss";

import { brandTheme } from "@amzn/stencil-design-tokens/js/web/theme";
import { StencilProvider } from "@amzn/stencil-react-components/context";
import { View } from "@amzn/stencil-react-components/layout";
import {
  Link,
  LinkTextDecorationOptions,
} from "@amzn/stencil-react-components/link";
import { Text } from "@amzn/stencil-react-components/text";
import createCache, { EmotionCache } from "@emotion/cache";
import { trackStructEvent } from "@snowplow/browser-tracker";
import { useEffect, useMemo, useState } from "react";
import { IntlProvider } from "react-intl";

import { EmbeddedMetric, Logger } from "@/apis/Logger";
import { SnowplowTracker } from "@/helpers/snowplow";
import { appOnCloseFunction } from "@/helpers/WindowEventListenerMethods";
import { useConsentLanguage } from "@/hooks/useConsentLanguage";
import { useSelectedLanguage } from "@/hooks/useSelectedLanguage";
import { loadTranslations } from "@/i18n/loadTranslations";
import { FeatureFlagsProvider } from "@/stores/contexts/FeatureFlagsContext";
import {
  ThemeModeProvider,
  useThemeMode,
} from "@/stores/contexts/ThemeModeContext";
import { SystemInfo } from "@/types/common";
import { Day1EventAction, Day1EventCategory } from "@/types/snowplow-events";

import { LOCALES } from "../i18n/locales";
import { ErrorBoundary } from "./ErrorBoundary";
import Home from "./Home";

const cache: EmotionCache = createCache({
  key: "nheemotioncache",
  // TODO: Generate an unique nonce every time when the page loads or use other solutions besides using nonce
  // https://sim.amazon.com/issues/NHE-928
  nonce: "NjIxOWQ1MGMtNTcxNS00YTMzLThkOTUtNTUzMjU1MjZmZjY4",
});

export default function App(): JSX.Element {
  const currentLocale = useSelectedLanguage();
  // Default to use en-US if we can't find any matching locale code

  const ivvConsentLanguage = useConsentLanguage(currentLocale);
  const [messages, setMessages] = useState<Record<string, string>>({});

  useEffect(() => {
    loadTranslations(currentLocale)
      .then((msgObj) => {
        setMessages(msgObj);
        return msgObj;
      })
      .catch((error) => {
        console.error("Failed to load translations:", error);
      });
  }, [currentLocale]);

  const localizedMessages = useMemo(
    () => ({
      ...messages,
      ...ivvConsentLanguage,
    }),
    [messages, ivvConsentLanguage]
  );

  useEffect(() => {
    // Initialize an instance immediately on load.
    SnowplowTracker.instance;
    trackStructEvent({
      category: Day1EventCategory.Header,
      action: Day1EventAction.ThemeLoaded,
      property: (
        localStorage.getItem("ezo-theme-mode") || "dark"
      ).toUpperCase(),
    });
    try {
      if (window.electronAPI) {
        window.electronAPI
          .getSystemInfo()
          .then((systemInfo: SystemInfo) => {
            if (systemInfo) {
              SnowplowTracker.systemInfo = systemInfo;
              Logger.systemInfo = systemInfo;
              return systemInfo;
            } else {
              return;
            }
          })
          .catch(() => {
            // Ignoring error since this API is only available from the EZO electron app.
          });
      }
    } catch (error) {
      // Ignoring error since this API is only available from the EZO electron app.
    }
    Logger.publishInfoMetric(new EmbeddedMetric("AppOpened", "Count", 1));

    /*
     * Capture refresh or page close events before the page closes with the best effort. Note that
     * this event is not captured properly using just useEffect clean up.
     */
    window.addEventListener("beforeunload", appOnCloseFunction, { once: true });
  }, []);

  return (
    <IntlProvider
      messages={localizedMessages}
      locale={currentLocale}
      defaultLocale={LOCALES.ENGLISH}
      defaultRichTextElements={{
        bold: /* istanbul ignore next: We use a custom IntlProvider in the tests */ (
          chunk
        ) => (
          <Text fontWeight="bold" display="inline">
            {chunk}
          </Text>
        ),
        i: /* istanbul ignore next: We use a custom IntlProvider in the tests */ (
          chunk
        ) => (
          <Text fontWeight="regular" display="inline">
            <i>{chunk}</i>
          </Text>
        ),
        ol: (chunk) => <ol type="1">{chunk}</ol>,
        ul: (chunk) => <ul>{chunk}</ul>,
        li: (chunk) => (
          <li>
            <Text fontWeight="regular" display="inline">
              {chunk}
            </Text>
          </li>
        ),
        a: ({ 0: url }) => (
          <Link
            className="wizard-link"
            textDecoration={LinkTextDecorationOptions.None}
            href={url as string}
            target="_blank"
            rel="noreferrer"
            fontWeight="regular"
          >
            {url}
          </Link>
        ),
        b: /* istanbul ignore next: We use a custom IntlProvider in the tests */ (
          chunk: unknown
        ) => (
          <Text fontWeight="bold" display="inline">
            {chunk}
          </Text>
        ),
      }}
    >
      {/*
        React-Intl is broken if the StencilProvider is not wrapped by the IntlProvider.
        https://stencil.a2z.com/for-developers/onboarding/brazil#configuring-live-announcements
      */}
      <ThemeModeProvider>
        <ThemedApp />
      </ThemeModeProvider>
    </IntlProvider>
  );
}

function ThemedApp(): JSX.Element {
  const { themeMode } = useThemeMode();

  return (
    <StencilProvider
      emotionCache={cache}
      themeMode={themeMode}
      theme={brandTheme}
    >
      <View
        backgroundColor="color.surface.bg-default"
        color="color.text.primary"
        minHeight="100vh"
      >
        <FeatureFlagsProvider>
          <ErrorBoundary>
            <Home />
          </ErrorBoundary>
        </FeatureFlagsProvider>
      </View>
    </StencilProvider>
  );
}
